import { AlertCircle, Check, Loader2, Lock } from "lucide-react";
import { useId } from "react";

import { Button } from "components/ui/button";
import { Textarea } from "components/ui/textarea";

import { PERSON_NOTE_MAX_LENGTH } from "./schemas";
import {
  usePersonNote,
  type NoteSaveStatus,
  type PersonNoteTarget,
} from "./use-person-note";

/** Show the counter once the note is this close to the cap. */
const COUNTER_THRESHOLD = PERSON_NOTE_MAX_LENGTH - 200;

/**
 * The private note textarea with its save state. Used on the profile sheet and
 * on the "My notes" page, so both write through the same autosave.
 */
export function PersonNoteEditor({
  target,
  autoFocus = false,
}: {
  target: PersonNoteTarget;
  autoFocus?: boolean;
}) {
  const note = usePersonNote(target);
  const fieldId = useId();
  const hintId = useId();
  const loading = note.status === "loading";

  if (note.status === "load-error") {
    return (
      <div
        className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground"
        data-testid="person-note-load-error"
      >
        <AlertCircle className="h-4 w-4 shrink-0 text-destructive-text dark:text-hb-red-text" />
        <span className="min-w-0 flex-1">Could not load your note.</span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="max-sm:h-11"
          onClick={note.retry}
        >
          Try again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <label
        htmlFor={fieldId}
        className="block text-sm font-semibold text-foreground"
      >
        My private note
      </label>
      <p
        id={hintId}
        className="flex items-center gap-1.5 text-xs text-muted-foreground"
      >
        <Lock className="h-3.5 w-3.5 shrink-0" />
        Only you can see this. It stays after the event.
      </p>
      <Textarea
        id={fieldId}
        value={note.text}
        onChange={(event) => note.setText(event.target.value)}
        onBlur={note.flush}
        disabled={loading}
        maxLength={PERSON_NOTE_MAX_LENGTH}
        rows={5}
        // 16px: iOS Safari zooms the page into any field set smaller, and
        // then leaves it zoomed after the keyboard closes.
        className="resize-y text-base"
        placeholder={
          loading ? "Loading…" : "Where you met, what to follow up on"
        }
        autoCapitalize="sentences"
        autoFocus={autoFocus}
        aria-describedby={hintId}
        data-testid="person-note-input"
      />
      <div className="flex min-h-11 flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs">
        <SaveStatus status={note.status} onRetry={note.retry} />
        {note.text.length >= COUNTER_THRESHOLD && (
          <span
            className="tabular-nums text-muted-foreground"
            data-testid="person-note-counter"
          >
            {note.text.length} / {PERSON_NOTE_MAX_LENGTH}
          </span>
        )}
      </div>
    </div>
  );
}

function SaveStatus({
  status,
  onRetry,
}: {
  status: NoteSaveStatus;
  onRetry: () => void;
}) {
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
      {/* A live region, so a screen reader hears "Saved" without moving
          focus. The retry button sits outside it: a control inside a live
          region is announced as text and lost as a control. */}
      <span
        role="status"
        aria-live="polite"
        className="flex min-w-0 items-center gap-1.5 text-muted-foreground"
        data-testid="person-note-status"
        data-status={status}
      >
        {status === "saving" && (
          <>
            <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
            Saving…
          </>
        )}
        {status === "dirty" && "Unsaved changes"}
        {status === "saved" && (
          <>
            <Check className="h-3.5 w-3.5 shrink-0" />
            Saved
          </>
        )}
        {status === "cleared" && "Empty. Closing removes this note."}
        {status === "removed" && (
          <>
            <Check className="h-3.5 w-3.5 shrink-0" />
            Note removed
          </>
        )}
        {status === "error" && (
          <>
            <AlertCircle className="h-3.5 w-3.5 shrink-0 text-destructive-text dark:text-hb-red-text" />
            <span className="text-foreground">Not saved.</span>
          </>
        )}
      </span>
      {status === "error" && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="max-sm:h-11"
          onClick={onRetry}
          data-testid="person-note-retry"
        >
          Try again
        </Button>
      )}
    </div>
  );
}
